import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { useAuth } from '../lib/auth';

type Role = 'ADMIN' | 'QA_MANAGER' | 'AUDITOR' | 'READ_ONLY';

interface PublicUser {
  _id: string;
  email: string;
  name: string;
  role: Role;
  active: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

const ROLE_LABEL: Record<Role, string> = {
  ADMIN: 'Yönetici',
  QA_MANAGER: 'KYT',
  AUDITOR: 'Tetkikçi',
  READ_ONLY: 'Görüntüleyici',
};

const ROLE_BADGE: Record<Role, string> = {
  ADMIN: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  QA_MANAGER: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
  AUDITOR: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  READ_ONLY: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

export function UsersPage() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const list = useQuery<{ items: PublicUser[]; total: number }>({
    queryKey: ['users', 'list'],
    queryFn: () => api.get('/v1/users'),
  });

  const create = useMutation({
    mutationFn: (payload: unknown) => api.post<PublicUser>('/v1/users', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users', 'list'] });
      setShowForm(false);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: unknown }) =>
      api.patch<PublicUser>(`/v1/users/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users', 'list'] });
      setEditingId(null);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/v1/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users', 'list'] }),
  });

  return (
    <div>
      <PageHeader
        title="Kullanıcı Yönetimi"
        subtitle="Sistem kullanıcıları (yalnızca ADMIN görür)"
        clause="§7.5.3 (Kayıt kontrolü)"
        actions={
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              setShowForm((v) => !v);
              setEditingId(null);
            }}
          >
            {showForm ? 'Kapat' : 'Yeni Kullanıcı'}
          </button>
        }
      />

      {showForm && (
        <div className="card mb-4 p-5">
          <h2 className="mb-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
            Yeni Kullanıcı
          </h2>
          <UserForm
            mode="create"
            busy={create.isPending}
            error={create.error instanceof Error ? create.error.message : null}
            onSubmit={(payload) => create.mutate(payload)}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {list.isLoading && (
        <div className="card p-6 text-sm text-slate-500 dark:text-slate-400">Yükleniyor…</div>
      )}
      {list.isError && (
        <div className="card p-6 text-sm text-rose-700 dark:text-rose-300">
          {list.error instanceof Error ? list.error.message : 'Hata'}
        </div>
      )}

      {list.data && (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-800">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left">Ad</th>
                <th className="px-4 py-3 text-left">E-posta</th>
                <th className="px-4 py-3 text-left">Rol</th>
                <th className="px-4 py-3 text-left">Durum</th>
                <th className="px-4 py-3 text-left">Son Giriş</th>
                <th className="px-4 py-3 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.data.items.map((u) => {
                const isSelf = me?.sub === u._id;
                if (editingId === u._id) {
                  return (
                    <tr key={u._id} className="bg-slate-50/60 dark:bg-slate-800/30">
                      <td colSpan={6} className="p-4">
                        <UserForm
                          mode="edit"
                          initial={u}
                          busy={update.isPending}
                          error={
                            update.error instanceof Error ? update.error.message : null
                          }
                          onSubmit={(payload) =>
                            update.mutate({ id: u._id, patch: payload })
                          }
                          onCancel={() => setEditingId(null)}
                        />
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={u._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <td className="px-4 py-2 font-medium text-slate-800 dark:text-slate-200">
                      {u.name}
                      {isSelf && (
                        <span className="ml-2 text-xs text-slate-400">(siz)</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-700 dark:text-slate-300">
                      {u.email}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`badge ${ROLE_BADGE[u.role]}`}>{ROLE_LABEL[u.role]}</span>
                    </td>
                    <td className="px-4 py-2">
                      {u.active ? (
                        <span className="badge bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                          Aktif
                        </span>
                      ) : (
                        <span className="badge bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                          Pasif
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                      {u.lastLoginAt
                        ? new Date(u.lastLoginAt).toLocaleString('tr-TR')
                        : '—'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        className="text-xs text-brand-700 hover:underline dark:text-brand-300"
                        onClick={() => {
                          setShowForm(false);
                          setEditingId(u._id);
                        }}
                      >
                        Düzenle
                      </button>
                      {!isSelf && (
                        <button
                          type="button"
                          className="ml-3 text-xs text-rose-700 hover:underline dark:text-rose-400"
                          onClick={() => {
                            if (confirm(`${u.email} kullanıcısını silmek istediğinizden emin misiniz?`)) {
                              remove.mutate(u._id);
                            }
                          }}
                        >
                          Sil
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {list.data.items.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400"
                  >
                    Henüz kullanıcı yok.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-500 dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-400">
            {list.data.items.length} / {list.data.total} kullanıcı
          </div>
        </div>
      )}
    </div>
  );
}

interface UserFormProps {
  mode: 'create' | 'edit';
  initial?: PublicUser;
  busy: boolean;
  error: string | null;
  onSubmit: (payload: unknown) => void;
  onCancel: () => void;
}

function UserForm({ mode, initial, busy, error, onSubmit, onCancel }: UserFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [role, setRole] = useState<Role>(initial?.role ?? 'READ_ONLY');
  const [password, setPassword] = useState('');
  const [active, setActive] = useState(initial?.active ?? true);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (mode === 'create') {
      onSubmit({ name, email, role, password });
    } else {
      const patch: Record<string, unknown> = { name, role, active };
      if (password.trim().length > 0) patch.password = password;
      onSubmit(patch);
    }
  }

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
      <div>
        <label className="label" htmlFor="user-name">Ad Soyad</label>
        <input
          id="user-name"
          className="input"
          required
          maxLength={120}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div>
        <label className="label" htmlFor="user-email">E-posta</label>
        <input
          id="user-email"
          type="email"
          className="input"
          required
          value={email}
          disabled={mode === 'edit'}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div>
        <label className="label" htmlFor="user-role">Rol</label>
        <select
          id="user-role"
          className="input"
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
        >
          <option value="ADMIN">Yönetici (ADMIN)</option>
          <option value="QA_MANAGER">Kalite Yönetim Temsilcisi (KYT)</option>
          <option value="AUDITOR">İç Tetkikçi</option>
          <option value="READ_ONLY">Görüntüleyici</option>
        </select>
      </div>
      <div>
        <label className="label" htmlFor="user-password">
          Şifre {mode === 'edit' && '(boş bırakılırsa değişmez)'}
        </label>
        <input
          id="user-password"
          type="password"
          className="input"
          minLength={mode === 'create' ? 8 : 0}
          required={mode === 'create'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={mode === 'edit' ? '••••••••' : 'en az 8 karakter'}
        />
      </div>
      {mode === 'edit' && (
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 sm:col-span-2">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          Kullanıcı aktif
        </label>
      )}
      {error && (
        <div className="sm:col-span-2 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900">
          {error}
        </div>
      )}
      <div className="flex items-center gap-2 sm:col-span-2">
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Kaydediliyor…' : mode === 'create' ? 'Oluştur' : 'Güncelle'}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
          İptal
        </button>
      </div>
    </form>
  );
}
